import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useToggle, useTimeoutFn } from '@vueuse/core'

export type NavContext =
  | 'dashboard'
  | 'projects'
  | 'users'
  | 'content'
  | 'sales'
  | 'contacts'
  | 'invoicing'
  | 'comms'
  | 'ai'
  | 'audit-log'
  | 'system'

// Map route prefixes to navigation contexts
const routeContextMap: Array<{ prefix: string; context: NavContext }> = [
  { prefix: '/projects', context: 'projects' },
  { prefix: '/users', context: 'users' },
  { prefix: '/orchestration', context: 'ai' },
  { prefix: '/pages', context: 'content' },
  { prefix: '/menus', context: 'content' },
  { prefix: '/videos', context: 'content' },
  { prefix: '/categories', context: 'content' },
  { prefix: '/assets', context: 'content' },
  { prefix: '/products', context: 'sales' },
  { prefix: '/aurum', context: 'sales' },
  { prefix: '/customers', context: 'sales' },
  { prefix: '/orders', context: 'sales' },
  { prefix: '/tickets', context: 'sales' },
  { prefix: '/event-dates', context: 'sales' },
  { prefix: '/event-reservations', context: 'sales' },
  { prefix: '/contacts', context: 'contacts' },
  { prefix: '/contact-groups', context: 'contacts' },
  { prefix: '/invoicing', context: 'invoicing' },
  { prefix: '/notifications', context: 'comms' },
  { prefix: '/email-templates', context: 'comms' },
  { prefix: '/forms', context: 'content' },
  { prefix: '/audit-log', context: 'audit-log' },
  { prefix: '/settings', context: 'system' },
]

export type ModalType = 'confirm' | 'alert'

export type ConfirmModalConfig = {
  type?: ModalType
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  confirmType?: 'primary' | 'danger'
  onConfirm: () => void
  onCancel?: () => void
}

export const useAppStore = defineStore('app', () => {
  const [isMobileMenuOpen, toggleMobileMenu] = useToggle(false)
  const [showNotifications, toggleNotifications] = useToggle(false)

  // Active navigation context (which rail button / sidebar panel is active)
  const activeContext = ref<NavContext>('dashboard')
  const notifications = ref<
    {
      id: string
      message: string
      type: 'info' | 'success' | 'warning' | 'error'
    }[]
  >([])

  // Stav pro modální okno
  const isConfirmModalOpen = ref(false)
  const confirmModalConfig = ref<ConfirmModalConfig | null>(null)

  function addNotification(
    message: string,
    type: 'info' | 'success' | 'warning' | 'error' = 'info'
  ) {
    const id = Date.now().toString()
    notifications.value.push({ id, message, type })

    useTimeoutFn(() => {
      removeNotification(id)
    }, 5000)

    return id
  }

  function removeNotification(id: string) {
    notifications.value = notifications.value.filter((n) => n.id !== id)
  }

  // Metody pro práci s modálním dialogem
  function openConfirmModal(config: ConfirmModalConfig) {
    confirmModalConfig.value = {
      type: 'confirm',
      ...config
    }
    isConfirmModalOpen.value = true
  }
  
  // Metoda pro zobrazení alert dialogu
  function showAlert(config: Omit<ConfirmModalConfig, 'onCancel'>) {
    confirmModalConfig.value = {
      type: 'alert',
      ...config
    }
    isConfirmModalOpen.value = true
  }

  function closeConfirmModal() {
    isConfirmModalOpen.value = false
    // Počkáme na dokončení animace před resetováním konfigurace
    useTimeoutFn(() => {
      confirmModalConfig.value = null
    }, 300)
  }

  function handleConfirmAction() {
    if (confirmModalConfig.value?.onConfirm) {
      confirmModalConfig.value.onConfirm()
    }
    closeConfirmModal()
  }

  function handleCancelAction() {
    if (confirmModalConfig.value?.onCancel) {
      confirmModalConfig.value.onCancel()
    }
    closeConfirmModal()
  }

  function setActiveContext(context: NavContext) {
    activeContext.value = context
  }

  function syncContextFromRoute(path: string) {
    for (const entry of routeContextMap) {
      if (path === entry.prefix || path.startsWith(`${entry.prefix}/`)) {
        activeContext.value = entry.context
        return
      }
    }
    // Default: dashboard for root
    if (path === '/' || path === '') {
      activeContext.value = 'dashboard'
    }
  }

  return {
    isMobileMenuOpen,
    showNotifications,
    notifications,
    toggleMobileMenu,
    toggleNotifications,
    addNotification,
    removeNotification,

    // Navigation context
    activeContext,
    setActiveContext,
    syncContextFromRoute,

    // Export pro modální okno
    isConfirmModalOpen,
    confirmModalConfig,
    openConfirmModal,
    showAlert,
    closeConfirmModal,
    handleConfirmAction,
    handleCancelAction
  }
})
